/**
 * App install link resolution for the final onboarding screen.
 *
 * The invitation's own Branch link (`signedUpPatient.deepLink`, written by `invitePatient` via the
 * Branch REST API) is always preferred. The fallback exists because that field is absent on any
 * invitation that did not go through `invitePatient` — seeded fixtures, `copyUserToDev` copies, and
 * the `userAlreadyInstalledApp` branch of `prepareInvitationLinks`, which passes an old document's
 * (possibly missing) link straight through. Install is the last screen of the flow and the CTA is
 * its only exit, so a missing field must not be a dead end.
 *
 * A store link is NOT an acceptable fallback: `processDeepLink` in `physiofit_app` reads
 * `params.signUpId` and returns immediately without it, so a store link would drop the invitation
 * linkage — no `handlePatientAppDeeplinking`, no login token, no account — while looking like it
 * worked.
 *
 * UNVERIFIED (ERS-61): that Branch resolves a query-only link on the link domain into
 * link data carrying `signUpId`. The Android intent filters register both Branch hosts with no path
 * constraint, so the URL opens the app, but the parameter passthrough still needs a device test on
 * a fresh install and on a hot open. Until that passes, treat `linkSource: 'fallback'` in PostHog as
 * unproven rather than as a working path.
 */

import { resolveFirebaseEnv } from '../../../firebaseConfig';

/**
 * Branch link domains. Production uses the live instance; every other environment uses the test
 * instance, which is what dev invitations carry (`https://physiofit.test-app.link/<hash>`) and what
 * `RNBranch.useTestInstance()` selects in DEBUG builds of the patient app.
 */
const BRANCH_LINK_DOMAIN = {
  production: 'https://physiofit.app.link',
  test: 'https://physiofit.test-app.link',
} as const;

export type AppInstallLink = {
  url: string;
  /** `invitation` = the link `invitePatient` minted. `fallback` = built here from the signUpId. */
  source: 'invitation' | 'fallback';
};

/**
 * Resolves the link the "App installieren" CTA navigates to.
 *
 * @param {string | undefined} invitationDeepLink `signedUpPatient.deepLink`.
 * @param {string | undefined} signUpId The invitation id — the only parameter the app reads.
 * @return {Object | null} The link and where it came from, or null when neither source is usable.
 */
export function resolveAppInstallLink(
  invitationDeepLink: string | undefined,
  signUpId: string | undefined,
): AppInstallLink | null {
  const stored = invitationDeepLink?.trim();
  if (stored) {
    return { url: stored, source: 'invitation' };
  }

  const id = signUpId?.trim();
  if (!id) {
    return null;
  }

  // `resolveFirebaseEnv`, not `import.meta.env.MODE`: it is what already decides which Firebase
  // project — and therefore which Branch instance minted this environment's invitations — is in
  // play. Its own `?? 'development'` default degrades to the test domain, which is the safe way
  // round: a dev build can never send a patient at a production link.
  const domain =
    resolveFirebaseEnv() === 'production' ? BRANCH_LINK_DOMAIN.production : BRANCH_LINK_DOMAIN.test;
  return { url: `${domain}/?signUpId=${encodeURIComponent(id)}`, source: 'fallback' };
}
